import '../styles/variables.css'; 
import React, { useEffect, useState, useCallback } from 'react';
import api from '../utils/api';
import { TextBuilderParameters } from '../models/TextBuilderParameters';
import { FlexContainer, FlexColumn } from './common/Flex';
import JargonOutput from '../models/JargonOutput';
import JargonIpsumBox from './home/JargonIpsumBox';

import {LogoBox,Logo} from './home/LogoBox';
import { ControlBox, ControlBoxHeader, ControlBoxLabel } from './common/ControlBox';
import FlavorText from './common/FlavorText';
import StyleControlBox from './home/StyleControlBox';
import JargonControlBox from './home/JargonControlBox';
import ServingSize from './home/ServingSize';
import JargonButton from './common/JargonButton';
import {Footer} from './home/Footer';
import { JargonStatus } from './home/JargonStatus';
import { SettingsContainer } from './common/ControlBox';
import { ProcessingMessage } from './home/ProcessingMessage';
import { JargonContainer } from './home/JargonContainer';
import { IconBar } from './home/IconBar';
import { IconList } from './home/IconList';
import { Header, HeaderText } from './home/Header';
import { CopyButtonContainer } from './home/CopyButtonContainer';
import { CopyButton } from './home/CopyButton';




const Home: React.FC = () => {
  
  // Helper function to get CSS variable values as numbers
  const getCSSVar = (varName: string): number => {
    const value = getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
    return parseInt(value, 10);
  };
  
  const [jargonContent,setJargonContent] = useState<JargonOutput|null>(null);
  const [styleValue, setStyleValue] = useState<string>("");
  const [servingSize, setServingSize] = useState<string>("5");
  const [selectedJargons,setSelectedJargons]=useState<number[]>([]);
  const [showStatus, setShowStatus] = useState<boolean>(false);
  const [contentOpacity, setContentOpacity] = useState<number>(1);
  const [typingText, setTypingText] = useState<string>("");
  const [isTyping, setIsTyping] = useState<boolean>(false);
  const [showCopyButton, setShowCopyButton] = useState<boolean>(false);
  const [showProcessingMessage, setShowProcessingMessage] = useState<boolean>(false);
  const [hasEverShownStatus, setHasEverShownStatus] = useState<boolean>(false);
  const [hasEverShownProcessing, setHasEverShownProcessing] = useState<boolean>(false);
  const [copyButtonText, setCopyButtonText] = useState<string>("Copy to Clipboard");
  const [showEnjoyState, setShowEnjoyState] = useState<boolean>(false);
  const [jargonsLoaded, setJargonsLoaded] = useState<boolean>(false);
  const [stylesLoaded, setStylesLoaded] = useState<boolean>(false);

  const settingsLoaded = jargonsLoaded && stylesLoaded;

  const handleStyleChange = (newValue: string) => {
    setStyleValue(newValue);
  };
  const handleServingSizeChange = (newValue:string) =>{
    // Only allow numeric input (including empty string)
    if (newValue === '' || /^\d+$/.test(newValue)) {
      setServingSize(newValue);
    }
  }
  const handleJargonSelection = (id: number, checked: boolean) => {
    setSelectedJargons(prev =>
      checked ? [...prev, id] : prev.filter(j => j !== id)
    );
  };

  const handleCopyToClipboard = async () => {
    if (jargonContent) {
      try {
        await navigator.clipboard.writeText(jargonContent.paragraphs.join('\n\n'));
        
        // Fade out, change text and colors, then fade back in
        setShowEnjoyState(true); // Start color transition
        
        // Brief delay to allow color transition, then change text
        setTimeout(() => {
          setCopyButtonText("Enjoy!");
        }, getCSSVar('--fade-duration') / 2);
        
        // Reset after showing "Enjoy!" for a moment
        setTimeout(() => {
          setShowEnjoyState(false); // Start color transition back
          
          // Brief delay for color transition, then change text back
          setTimeout(() => {
            setCopyButtonText("Copy to Clipboard");
          }, getCSSVar('--fade-duration') / 2);
        }, 500);
      } catch (error) {
        console.error('Failed to copy to clipboard:', error);
      }
    }
  };

  const regenerateText = async () => {
    // Reset copy button when starting new generation
    setCopyButtonText("Copy to Clipboard");
    setShowEnjoyState(false);
    
    // Clear the typing text but keep the box visible
    setTypingText("");
    setIsTyping(false);
    
    // Skip delays and go straight to fetching new content
    try {
      let params : TextBuilderParameters = {
        jargons: selectedJargons,
        paragraphCount: parseInt(servingSize ?? '5', 10),
        styleId: parseInt(styleValue ?? '', 10)
      }
      let result = await api.post('/builder/',params);

      // Prepare content for typing animation
      const fullText = result.data.paragraphs.join('\n\n');
      setJargonContent(result.data);
      setContentOpacity(1);
      
      // Show copy button (will stay visible for subsequent regenerations)
      setShowCopyButton(true);
      
      // Start typewriter effect immediately
      await typeText(fullText);
    } catch (error) {
      console.error('Error generating jargon:', error);
    }
  };

  const handleAgainClick = () => {
    // Scroll to jargon output box
    const jargonOutputElement = document.querySelector('[data-jargon-output]');
    if (jargonOutputElement) {
      jargonOutputElement.scrollIntoView({ behavior: 'smooth' });
    }
    // Regenerate text without delays
    regenerateText();
  };

  const typeText = (fullText: string, charSpeed: number = getCSSVar('--typing-char-speed'), paragraphPause: number = getCSSVar('--typing-paragraph-pause')) => {
    return new Promise<void>((resolve) => {
      setTypingText("");
      setIsTyping(true);
      let currentIndex = 0;
      
      const typeNextChar = () => {
        if (currentIndex < fullText.length) {
          const currentChar = fullText[currentIndex];
          const previousChars = fullText.slice(0, currentIndex);
          
          setTypingText(fullText.slice(0, currentIndex + 1));
          currentIndex++;
          
          // Check if we just completed a paragraph (double newline)
          if (currentChar === '\n' && previousChars.endsWith('\n')) {
            // Pause longer after paragraph
            setTimeout(typeNextChar, paragraphPause);
          } else {
            // Normal character speed
            setTimeout(typeNextChar, charSpeed);
          }
        } else {
          setIsTyping(false);
          resolve();
        }
      };
      
      typeNextChar();
    });
  };

  const buildText = async () => {
    // Reset copy button when starting new generation
    setCopyButtonText("Copy to Clipboard");
    setShowEnjoyState(false);
    
    // If content already exists, clear the typing text but keep the box visible
    if (jargonContent) {
      setTypingText("");
      setIsTyping(false);
    }

    // Show status box
    setShowStatus(true);
    setHasEverShownStatus(true);
    
    // Wait a moment, then show processing message
    await new Promise(resolve => setTimeout(resolve, getCSSVar('--status-delay')));
    setShowProcessingMessage(true);
    setHasEverShownProcessing(true);
    
    // Wait a bit more, then fetch new content
    await new Promise(resolve => setTimeout(resolve, getCSSVar('--processing-delay')));
    
    try {
      let params : TextBuilderParameters = {
        jargons: selectedJargons,
        paragraphCount: parseInt(servingSize ?? '5', 10),
        styleId: parseInt(styleValue ?? '', 10)
      }
      let result = await api.post('/builder/',params);

      // Prepare content for typing animation
      const fullText = result.data.paragraphs.join('\n\n');
      setJargonContent(result.data);
      setContentOpacity(1);
      
      // Show copy button (will stay visible for subsequent regenerations)
      setShowCopyButton(true);
      
      // Start typewriter effect
      await typeText(fullText);
    } catch (error) {
      console.error('Error generating jargon:', error);
    }
  }
  return (
    <JargonContainer>
      <Header>
      <LogoBox>
        <Logo src="/jargon-ipsum.png" />
      </LogoBox>
      <HeaderText scaleSmall={1}>
        <IconBar><IconList /></IconBar>
        <div>
          <p>Generate <FlavorText>industry-relevant placeholder</FlavorText> for your next project.</p>
        </div>
        </HeaderText>
      </Header>

      <div style={{ visibility: settingsLoaded ? 'visible' : 'hidden', minHeight: settingsLoaded ? 'auto' : '400px' }}>
        <FlexContainer id="settings-container">
          <JargonControlBox onChange={handleJargonSelection} selectedJargons={selectedJargons} onLoaded={() => setJargonsLoaded(true)} />
          <ControlBox scaleLarge={1} scaleMedium={1}>
            <ControlBoxHeader>
              <ControlBoxLabel>2.</ControlBoxLabel>
              Serving Size
            </ControlBoxHeader>
            <p>How many paragraphs will you need? Don't be shy.</p>
            <ServingSize value={servingSize} onChange={handleServingSizeChange} /> <label>Paragraphs</label>
          </ControlBox>


        <SettingsContainer>
            <StyleControlBox value={styleValue} onChange={handleStyleChange} onLoaded={() => setStylesLoaded(true)} />
        </SettingsContainer>
        </FlexContainer>
        <JargonButton onClick={buildText}>Give Me Jargon Ipsum</JargonButton>
      </div>
      <JargonStatus show={showStatus} hasEverShown={hasEverShownStatus}/>
      <ProcessingMessage show={showProcessingMessage} hasEverShown={hasEverShownProcessing}>
        <p><a href="https://halo22.net/jargon-ipsum?utm_source=ji&utm_content=body" target="_blank">HALO 22</a> accepts full responsibility for Jargon Ipsum. What you do with it is entirely your problem.
        </p>
      </ProcessingMessage>

      <div data-jargon-output>
        <JargonIpsumBox content={jargonContent} opacity={contentOpacity} typingText={typingText} isTyping={isTyping}></JargonIpsumBox>
      </div>
      
      <CopyButtonContainer show={showCopyButton}>
        <JargonButton onClick={handleAgainClick} disabled={isTyping}>Again!</JargonButton>
        <CopyButton onClick={handleCopyToClipboard} disabled={isTyping} showEnjoy={showEnjoyState}>
          {copyButtonText}
        </CopyButton>
      </CopyButtonContainer>
              
      </JargonContainer>
  );
}

export default Home;